/**
 * Returns a sorted list of IANA timezones. Prefers the runtime's
 * `Intl.supportedValuesOf('timeZone')` (widely available since mid-2022);
 * falls back to a curated list covering major regions so the UI always has
 * usable options on older engines.
 */
const FALLBACK_TIMEZONES: string[] = [
  'UTC',
  // Americas
  'America/Anchorage', 'America/Los_Angeles', 'America/Denver', 'America/Phoenix',
  'America/Chicago', 'America/New_York', 'America/Toronto', 'America/Mexico_City',
  'America/Bogota', 'America/Lima', 'America/Sao_Paulo', 'America/Buenos_Aires',
  'America/Santiago', 'America/Halifax',
  // Europe
  'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris',
  'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Berlin', 'Europe/Zurich',
  'Europe/Rome', 'Europe/Vienna', 'Europe/Stockholm', 'Europe/Oslo',
  'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Warsaw', 'Europe/Prague',
  'Europe/Budapest', 'Europe/Athens', 'Europe/Istanbul', 'Europe/Moscow',
  'Europe/Kyiv',
  // Africa
  'Africa/Lagos', 'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Nairobi',
  // Middle East / Asia
  'Asia/Jerusalem', 'Asia/Dubai', 'Asia/Tehran', 'Asia/Karachi', 'Asia/Kolkata',
  'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Singapore', 'Asia/Shanghai',
  'Asia/Hong_Kong', 'Asia/Taipei', 'Asia/Seoul', 'Asia/Tokyo',
  // Oceania
  'Australia/Perth', 'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Sydney',
  'Pacific/Auckland',
];

export function listTimezones(): string[] {
  type IntlWithSupported = typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  const intl = Intl as IntlWithSupported;
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      const tz = intl.supportedValuesOf('timeZone');
      if (Array.isArray(tz) && tz.length > 0) return [...tz].sort();
    } catch {
      // fall through
    }
  }
  return [...FALLBACK_TIMEZONES].sort();
}

export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Returns a "GMT±HH:MM" label for an IANA timezone at the current moment.
 * Used to group zones with equivalent offsets so the user first picks an
 * offset and then narrows to a specific location.
 */
export function getTimezoneOffsetLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const offset = parts.find(p => p.type === 'timeZoneName')?.value;
    if (!offset) return 'GMT';
    // Normalize "GMT" (no offset) to "GMT+00:00" so it sorts predictably.
    return offset === 'GMT' ? 'GMT+00:00' : offset;
  } catch {
    return 'GMT+00:00';
  }
}

/** Minutes since UTC for a "GMT±HH:MM" label. */
function offsetToMinutes(label: string): number {
  const m = /GMT([+-])(\d{1,2}):?(\d{2})?/.exec(label);
  if (!m) return 0;
  const sign = m[1] === '-' ? -1 : 1;
  const hours = parseInt(m[2], 10);
  const mins = m[3] ? parseInt(m[3], 10) : 0;
  return sign * (hours * 60 + mins);
}

export interface TimezoneGroup {
  offset: string;
  zones: string[];
}

/**
 * Groups every IANA zone by its current UTC offset, sorted west-to-east.
 * Each group's zones are also sorted alphabetically for stable UI order.
 */
export function groupTimezonesByOffset(): TimezoneGroup[] {
  const zones = listTimezones();
  const map = new Map<string, string[]>();
  for (const z of zones) {
    const offset = getTimezoneOffsetLabel(z);
    const existing = map.get(offset);
    if (existing) existing.push(z);
    else map.set(offset, [z]);
  }
  return Array.from(map.entries())
    .map(([offset, zs]) => ({ offset, zones: zs.sort() }))
    .sort((a, b) => offsetToMinutes(a.offset) - offsetToMinutes(b.offset));
}

/** Turn "America/New_York" into "New York" for a cleaner location label. */
export function formatTimezoneLocation(timeZone: string): string {
  const lastSegment = timeZone.split('/').pop() || timeZone;
  return lastSegment.replace(/_/g, ' ');
}
